import { ID3Writer } from 'browser-id3-writer';

const DARK_TORUS_ARTIST = 'Dark Torus';
const DARK_TORUS_SOURCE_URL = 'https://darktorus.com/';
/** Terms of use / legal page — use in metadata so players and users can open it */
const DARK_TORUS_LEGAL_URL = 'https://darktorus.com/legal';
const DARK_TORUS_PUBLISHER = 'Dark Torus / darktorus.com';

/** TCOP — copyright notice */
const DARK_TORUS_COPYRIGHT =
  '© Dark Torus. All rights reserved. darktorus.com';

/** COMM — comment: Dark Torus + track ID + darktorus.com. Exported for Firestore sync. */
export function buildComment(trackId: string): string {
  return `Dark Torus | ${trackId} | darktorus.com`;
}

/** TXXX copyright_notice — extended copyright notice */
const DARK_TORUS_COPYRIGHT_NOTICE =
  '© Dark Torus. All rights reserved. This track is provided for personal, non-commercial use only. Redistribution, resale or commercial use without permission is prohibited. darktorus.com';

/** TXXX license — terms of use URL (legal page) */
const DARK_TORUS_LICENSE = DARK_TORUS_LEGAL_URL;

export interface Mp3MetadataToWrite {
  title: string;
  album: string;
  duration: number;
  pictureBlob: Blob | null;
  /** Year — current year at upload time */
  year: number;
  /** Genre/category selected by user */
  genre: string;
  /** Track ID from our database (Firestore) */
  trackId: string;
}

/**
 * Overwrites ID3 tags in MP3. All existing tags are removed.
 * Comment (COMM + TXXX comment) is always overwritten with our values.
 * Preserves: title, album, duration, picture.
 * Adds: artist, TYER, TCON, TCOP, TPUB, WOAF, COMM, TXXX (comment, source_url, copyright_notice, license → /legal).
 */
export async function writeMp3Metadata(
  file: File,
  metadata: Mp3MetadataToWrite
): Promise<Blob> {
  const arrayBuffer = await file.arrayBuffer();
  const writer = new ID3Writer(arrayBuffer);

  writer.setFrame('TIT2', metadata.title);
  writer.setFrame('TPE1', [DARK_TORUS_ARTIST]);
  writer.setFrame('TALB', metadata.album);
  writer.setFrame('TLEN', metadata.duration * 1000); // milliseconds
  writer.setFrame('TYER', metadata.year);
  writer.setFrame('TCON', [metadata.genre]);

  writer.setFrame('TCOP', DARK_TORUS_COPYRIGHT);
  writer.setFrame('TPUB', DARK_TORUS_PUBLISHER);
  writer.setFrame('WOAF', DARK_TORUS_SOURCE_URL);

  const commentText = buildComment(metadata.trackId);
  writer.setFrame('COMM', {
    description: '',
    text: commentText,
    language: 'eng',
  });

  writer
    .setFrame('TXXX', { description: 'comment', value: commentText })
    .setFrame('TXXX', { description: 'source_url', value: DARK_TORUS_SOURCE_URL })
    .setFrame('TXXX', { description: 'copyright_notice', value: DARK_TORUS_COPYRIGHT_NOTICE })
    .setFrame('TXXX', { description: 'license', value: DARK_TORUS_LICENSE });

  if (metadata.pictureBlob) {
    const pictureArrayBuffer = await metadata.pictureBlob.arrayBuffer();
    writer.setFrame('APIC', {
      type: 3,
      data: pictureArrayBuffer,
      description: 'Cover',
    });
  }

  writer.addTag();
  return writer.getBlob();
}
